"use client";

import { safeGetItem, safeRemoveItem } from "@/lib/storage/safe-storage";
import { useEffect, useRef, useState } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { createClient } from "@/lib/supabase/client";
import { savePostAuthRedirect } from "@/lib/auth-redirect";
import {
  buildFallbackNameMetadata,
  buildPostHogPersonProperties,
} from "@/lib/auth-user-metadata";
import { getAuthSiteOrigin } from "@/lib/auth-site";
import { readHomePromptStash } from "@/lib/home-prompt-stash";
import { readCloneIntent } from "@/lib/home-clone-stash";
import { GoogleSignInButton } from "@/components/auth/google-sign-in-button";
import { FacebookSignInButton } from "@/components/auth/facebook-sign-in-button";
import { LinkedInSignInButton } from "@/components/auth/linkedin-sign-in-button";
import Image from "next/image";
import { cn } from "@/lib/utils";
import { isValidEmailStructure } from "@/lib/validation/email";
import { getEmailSuggestion } from "@/lib/validation/email-suggestion";
import { ArrowLeft, Eye, EyeOff, X } from "lucide-react";
import { linkUtmToUser } from "@/lib/utm";
import {
  trackSignup,
  identifyUser,
  trackSignupModalOpened,
  trackSignupModalDismissed,
  trackSignupFailed,
  trackSignupRecovered,
} from "@/lib/analytics";
import { useResetOnBfcache } from "@/lib/use-reset-on-bfcache";
import {
  authErrorStatus,
  isBrowserOffline,
  isTransientAuthError,
  probeAccountExists,
  waitForConnectivity,
  withTimeout,
  CONNECTIVITY_WAIT_MS,
  NETWORK_RETRY_BASE_MS,
  RECOVERY_ATTEMPT_TIMEOUT_MS,
  RECOVERY_BUDGET_MS,
  SIGNUP_REQUEST_TIMEOUT_MS,
} from "@/lib/auth/signup-recovery";
import { isStudentEmail } from "@/lib/student-domains.mjs";
import { Dialog as DialogPrimitive } from "@base-ui/react/dialog";
import type { User } from "@supabase/supabase-js";

const fontTight = "font-[family-name:var(--font-inter-tight),sans-serif]";

// Figma mobile (node 2350:42030): 1px border-/-strong, rounded-full,
// gap-2 between icon (24px) and text, h-12, Rubik Medium 16/24 text-/-primary.
// Dark hexes are the dark-resolved tokens from Figma node 1696:16735
// (border-/-strong #334155, text-/-primary #f1f5f9), same set used by
// the login modal.
const socialBtnClass =
  "flex h-12 w-full items-center justify-center gap-2 rounded-full border border-[#CBD5E1] bg-white font-[family-name:var(--font-rubik),sans-serif] text-base font-medium leading-6 text-[#1A1A1B] transition-colors hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-[#334155] dark:bg-transparent dark:text-[#f1f5f9] dark:hover:bg-[#18212f]";

// Field per Figma node 2350:42050: h-[54px], 1px border-strong, rounded-[16px],
// p-4, body/default placeholder 16/24 text-tertiary. Dark: bg grey-dark-800
// #18212f, border #334155, placeholder #94a3b8, text #f1f5f9.
const inputClass =
  "block h-[54px] w-full rounded-2xl border border-[#CBD5E1] bg-white p-4 font-[family-name:var(--font-rubik),sans-serif] text-base font-normal leading-6 text-[#1A1A1B] outline-none placeholder:text-[#77869D] focus:border-emerald-600 focus:ring-2 focus:ring-emerald-600/20 dark:border-[#334155] dark:bg-[#18212f] dark:text-[#f1f5f9] dark:placeholder:text-[#94a3b8]";

const inputWithIconClass =
  "block h-[54px] w-full rounded-2xl border border-[#CBD5E1] bg-white pl-4 pr-11 font-[family-name:var(--font-rubik),sans-serif] text-base font-normal leading-6 text-[#1A1A1B] outline-none placeholder:text-[#77869D] focus:border-emerald-600 focus:ring-2 focus:ring-emerald-600/20 dark:border-[#334155] dark:bg-[#18212f] dark:text-[#f1f5f9] dark:placeholder:text-[#94a3b8]";

// Primary button per Figma: h-12, rounded-full, Rubik Medium 16/24 white.
// Active = the success gradient (PANDA-1779; creating an account is the
// encouraged action, not a warning). Disabled is a flat grey pill —
// #F1F5F9/#475569 in light, #18212f/#94A3B8 in dark. Label sits one step off
// the Figma disabled token in each theme so it clears 4.5:1; see login-modal.
const primaryBtnClass =
  "flex h-12 w-full items-center justify-center rounded-full bg-panda-gradient-success font-[family-name:var(--font-rubik),sans-serif] text-base font-medium leading-6 text-white shadow-md shadow-emerald-900/25 transition-opacity disabled:cursor-not-allowed disabled:bg-none disabled:bg-[#F1F5F9] disabled:text-[#475569] disabled:shadow-none dark:disabled:bg-[#18212f] dark:disabled:text-[#94A3B8]";

function safeNext(path: string): string {
  if (!path.startsWith("/") || path.startsWith("//")) return "/";
  return path;
}

// When the signup form actually performs a LOGIN — the entered email already
// has an account, so we sign the existing user in instead of creating one —
// they should land where any login lands: their projects dashboard. `safeNext`
// returns "/" for the "no explicit destination" case; map that to /projects.
// An explicit deep link (a non-"/" path) is preserved. This is used ONLY on the
// existing-account sign-in paths; a genuine new signup keeps its own default.
//
// EXCEPTION (mirrors the login modal): if a home-page action is waiting to
// resume after auth — a typed prompt or a "Clone this" intent — the login must
// land back on "/" so the homepage replay can consume it; those flows carry
// their payload in storage, not in `next`. Reading the stashes does not consume
// them.
function loginDestination(path: string): string {
  const safe = safeNext(path);
  if (safe !== "/") return safe;
  if (readHomePromptStash() || readCloneIntent()) return "/";
  return "/projects";
}

// Human-readable name for a Supabase identity provider.
function providerLabel(provider: string): string {
  switch (provider) {
    case "google":
      return "Google";
    case "facebook":
      return "Facebook";
    case "linkedin_oidc":
    case "linkedin":
      return "LinkedIn";
    default:
      return provider.charAt(0).toUpperCase() + provider.slice(1);
  }
}

// Where the verification magic link lands the user after /auth/confirm sets
// the session. `next` carries the pre-auth destination — e.g. "/?prompt=…&
// templateId=…" — so the email/password flow resumes the submitted prompt the
// same way SSO does via buildOAuthCallbackUrl(). Without this the link
// hardcoded "/", dropping the prompt after verification (PANDA-1284). Only
// same-origin relative paths pass through; anything else falls back to "/".
function buildEmailRedirectTo(next?: string): string | undefined {
  const origin = getAuthSiteOrigin();
  if (!origin) return undefined;
  const safe = next && next.startsWith("/") && !next.startsWith("//") ? next : "/";
  return `${origin}/auth/confirm?next=${encodeURIComponent(safe)}`;
}

// isTransientAuthError now lives in lib/auth/signup-recovery.ts so the sibling
// auth surfaces can share one definition of "the network ate this request".

// Keep readable validation messages (e.g. "Password should be at least 8
// characters") but never surface a raw Supabase URL/hostname to the user.
function friendlySignupError(message: string): string {
  if (/supabase\.co|https?:\/\//i.test(message)) {
    return "Something went wrong creating your account. Please try again.";
  }
  return message;
}

// ── Failure copy ───────────────────────────────────────────────────────────
// One message per DISTINCT situation. The old single "We couldn't reach our
// servers" blamed the user's connection for every outcome — including the one
// where their account had in fact been created — which is the false failure
// state this fix exists to remove. Nothing here claims a network problem
// unless the browser itself reports one.

// navigator.onLine === false: the device genuinely has no route out.
const OFFLINE_MESSAGE =
  "You're offline right now. Reconnect and tap Create an account again — your details are still here.";

// The request died while the browser reported it was online, so the failure was
// ours, not theirs (same policy as classifyBuildError in lib/api.ts).
const SERVER_MESSAGE =
  "Something went wrong on our end. Your details are still here — try again in a moment.";

const RATE_LIMITED_MESSAGE =
  "Too many tries in a row. Wait about a minute, then try again.";

// The account IS there — /api/auth/check-email confirmed it — we just couldn't
// establish the session. Shown on the sign-in step, one password from being in.
const ACCOUNT_CREATED_MESSAGE =
  "Your account was created, but we couldn't finish signing you in. Enter your password to continue.";

// A recovered session whose email is not the address that was typed: someone
// else is signed in on this device (a shared phone).
const OTHER_USER_MESSAGE =
  "Someone else is already signed in on this device. Sign out first, then create your account.";

// Crossfades between the open-eyes panda and the eyes-covered panda when
// any password field has focus. The closed-eyes asset is square-framed,
// the lockup is wide — hence the different `object-position` values.
function PandaAuthAvatar({ eyesClosed }: { eyesClosed: boolean }) {
  return (
    <div className="relative h-21 w-21 sm:h-20 sm:w-20">
      <Image
        src="/panda-lockup.png"
        alt="Panda"
        width={448}
        height={448}
        priority
        className={cn(
          "absolute inset-0 h-full w-full rounded-full object-cover object-left transition-opacity duration-200 ease-out",
          eyesClosed ? "opacity-0" : "opacity-100",
        )}
      />
      <Image
        src="/eye_close.png"
        alt=""
        width={448}
        height={448}
        priority
        aria-hidden
        className={cn(
          "absolute inset-0 h-full w-full rounded-full object-cover object-center transition-opacity duration-200 ease-out",
          eyesClosed ? "opacity-100" : "opacity-0",
        )}
      />
    </div>
  );
}

function EyeToggle({
  visible,
  onToggle,
}: {
  visible: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      className="absolute right-3 top-1/2 -translate-y-1/2 text-[#9CA3AF] transition-colors hover:text-[#101010] dark:text-[#94a3b8] dark:hover:text-[#f1f5f9]"
      aria-label={visible ? "Hide password" : "Show password"}
    >
      {visible ? (
        <EyeOff className="h-5 w-5" strokeWidth={1.75} />
      ) : (
        <Eye className="h-5 w-5" strokeWidth={1.75} />
      )}
    </button>
  );
}

type SignupModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Where to send the user after successful signup. Defaults to "/". */
  next?: string;
  /**
   * Pre-fills the email step. Set by the login modal's "No account found —
   * Sign up" handoff so the address the user just typed carries over instead
   * of being retyped. Stays editable; "" (a direct "Sign up" click) seeds
   * nothing.
   */
  initialEmail?: string;
  /**
   * The CTA/surface that opened this modal (e.g. "home_nav"). Threaded onto
   * every event in the signup funnel so conversion and drop-off can be broken
   * down per entry point. Defaults to "unspecified" rather than being
   * required, so existing call sites keep working.
   */
  source?: string;
};

type Step = "email" | "details" | "signin";

export function SignupModal({
  open,
  onOpenChange,
  next = "/",
  initialEmail,
  source = "unspecified",
}: SignupModalProps) {
  const [step, setStep] = useState<Step>("email");
  // Seeded for the case where the modal is first rendered already open; the
  // usual closed→open handoff is picked up by the sync-during-render below.
  const [email, setEmail] = useState(initialEmail?.trim() ?? "");
  const [emailSuggestion, setEmailSuggestion] = useState<string | null>(null);
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  // Sign-in methods of an existing account hit at the email step. Empty =
  // unknown (treated as a password account). OAuth-only → we show that
  // provider's button instead of a password field.
  const [existingProviders, setExistingProviders] = useState<string[]>([]);
  const [passwordFocused, setPasswordFocused] = useState(false);
  // Tracks the previous `open` so the seeding below runs once per closed→open
  // transition (React's recommended alternative to a prop→state effect — see
  // https://react.dev/learn/you-might-not-need-an-effect).
  const [wasOpen, setWasOpen] = useState(open);

  const router = useRouter();

  // ── Signup funnel instrumentation ──────────────────────────────────────────
  // Set on every path that authenticates the user, so closing the modal after
  // a success isn't miscounted as abandonment. Reset on each fresh open — this
  // modal never unmounts, so its state outlives a close.
  const authCompletedRef = useRef(false);
  // Tracked in a ref rather than derived from `wasOpen` above: this must fire
  // only on a real open↔close transition, never on unmount (an OAuth click
  // navigates away, which would otherwise log a phantom drop-off).
  const prevOpenRef = useRef(open);
  // What the email-step pre-flight authoritatively told us. "no_account" ONLY
  // when check-email answered checked:true + exists:false — that is the single
  // piece of evidence that lets a RECOVERED session be called a new account
  // rather than a sign-in to one that already existed. Getting this wrong would
  // both inflate the signup funnel and alias two real people together.
  const preflightRef = useRef<"no_account" | "unknown">("unknown");
  // One post-signup side-effect chain per modal open. Without it a second
  // submit, or a recovery that races the original request, would double-fire
  // user_signed_up, the Meta conversion, and the verification magic link.
  const signupTailFiredRef = useRef(false);
  // Set when the user closes the modal (or the page is restored from bfcache)
  // mid-recovery. Checked at every await boundary: we still finish the account,
  // we just never yank a user who has already navigated away.
  const abortedRef = useRef(false);

  // Tapping the in-modal Terms/Privacy links and hitting Back freezes this page
  // into the bfcache with `loading` stuck true, leaving the submit button dead
  // forever. All three OAuth buttons already guard this way.
  useResetOnBfcache(() => {
    setLoading(false);
    abortedRef.current = true;
  });

  // `step` is a dependency purely so a dismissal reports the step the user
  // actually reached. Re-running on a step change is harmless: `open` hasn't
  // moved, so the transition guard below returns before emitting anything.
  useEffect(() => {
    const wasOpenBefore = prevOpenRef.current;
    prevOpenRef.current = open;
    if (open === wasOpenBefore) return;
    if (open) {
      authCompletedRef.current = false;
      preflightRef.current = "unknown";
      signupTailFiredRef.current = false;
      abortedRef.current = false;
      trackSignupModalOpened(source);
      return;
    }
    abortedRef.current = true;
    if (!authCompletedRef.current) {
      trackSignupModalDismissed({ source, reached_step: step });
    }
  }, [open, source, step]);

  // Debounced "did you mean?" — recompute the typo hint shortly after the user
  // stops typing, so it is visible BEFORE they click Continue (blur fires too
  // late: handleEmailContinue proceeds before the hint can be acted on). State
  // is set only inside the timeout, keeping this clear of set-state-in-effect.
  useEffect(() => {
    const value = email.trim();
    if (!value) return;
    const id = setTimeout(
      () => setEmailSuggestion(getEmailSuggestion(value)),
      400,
    );
    return () => clearTimeout(id);
  }, [email]);

  // Carry the login modal's email across the "No account found — Sign up"
  // handoff. Keyed on the closed→open transition rather than on `initialEmail`
  // changing, so re-entering the flow with the SAME address still re-seeds
  // after the user edited the field here (this modal never unmounts, so its
  // state outlives a close).
  //
  // The reset runs on EVERY closed→open transition, not just a seeded one:
  // every call site except the login-modal handoff passes no `initialEmail`, so
  // gating the reset on a seed meant a failure message, the typed password and
  // step="details" all survived a close and reappeared on the next open. A fix
  // that makes the error rarer but stickier would be a net regression.
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) {
      const seed = initialEmail?.trim();
      if (seed) setEmail(seed);
      // Land on the editable email step with nothing carried over from an
      // earlier attempt in this same never-unmounted modal.
      setStep("email");
      setPassword("");
      setError(null);
      setEmailSuggestion(null);
      setExistingProviders([]);
    }
  }

  function handlePwBlur() {
    setPasswordFocused(false);
  }

  async function handleEmailContinue(e: React.FormEvent) {
    e.preventDefault();
    const trimmed = email.trim();
    if (!isValidEmailStructure(trimmed)) {
      setError("Please enter a valid email address.");
      return;
    }
    setError(null);

    // Pre-flight existence check. Supabase only surfaces "already
    // registered" on signUp itself, so without this the user would
    // fill out the whole password / name step before finding out the
    // email's taken. When the email already exists we don't dead-end
    // them with a "go log in elsewhere" message — we route them
    // straight into a password-only sign-in step for that same email
    // (handleSignin). Network failure → fail open and proceed to the
    // signup details step (the signUp call is still the authoritative
    // gate, and its already-registered branch auto-logs-in too).
    setLoading(true);
    try {
      const res = await fetch("/api/auth/check-email", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: trimmed }),
      });
      if (res.ok) {
        const body = (await res.json()) as {
          exists?: boolean;
          providers?: string[];
          checked?: boolean;
        };
        // Remember whether this was an AUTHORITATIVE "no account exists". Only
        // that verdict can later justify calling a recovered session a brand-new
        // signup; `checked: false` (missing service key, lookup threw) must stay
        // "unknown" so we never fire user_signed_up for a pre-existing account.
        preflightRef.current =
          body?.checked === true && body?.exists === false
            ? "no_account"
            : "unknown";
        if (body?.exists === true) {
          setPassword("");
          setExistingProviders(
            Array.isArray(body.providers) ? body.providers : [],
          );
          setLoading(false);
          setStep("signin");
          return;
        }
      } else if (res.status === 400) {
        // The server rejected the address itself — bad structure or a
        // well-shaped but non-existent TLD like "gmial.cob" that the
        // client-side shape check can't catch (the public-suffix list is
        // server-only). Surface it inline and DO NOT advance to the details
        // step. Other non-ok statuses (429 rate-limit, 5xx) fall through and
        // let signUp do the gating, same as a network error below.
        const errBody = (await res.json().catch(() => null)) as {
          error?: string;
        } | null;
        if (errBody?.error === "invalid_email") {
          setError("Please enter a valid email address.");
          setLoading(false);
          return;
        }
      }
    } catch {
      // network issue — fall through and let signUp do the gating
    }
    setLoading(false);
    setStep("details");
  }

  function handleBackToEmail() {
    setStep("email");
    setError(null);
    setEmailSuggestion(null);
    setExistingProviders([]);
    // The address may be edited from here, so the previous verdict no longer
    // describes whatever gets submitted next.
    preflightRef.current = "unknown";
  }

  // Existing-account path: the email is already taken, so instead of
  // bouncing the user to a separate login page we sign them in right
  // here with the password they enter. Reached from the email-step
  // pre-flight (exists === true) and as the fallback when signUp()
  // reports "already registered" with a password that didn't match.
  async function handleSignin(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError(null);

    const supabase = createClient();
    const { error: signinError } = await supabase.auth.signInWithPassword({
      email: email.trim(),
      password,
    });

    if (signinError) {
      // This step is only ever reached for an account we know exists, so
      // `accountExists: true` is a fact here, not an inference.
      if (isTransientAuthError(signinError)) {
        const offline = isBrowserOffline();
        setError(offline ? OFFLINE_MESSAGE : SERVER_MESSAGE);
        trackSignupFailed({
          source,
          stage: "sign_in_after_create",
          reason: offline ? "offline" : "server_unreachable",
          status: authErrorStatus(signinError),
          attempts: 1,
          recovered: false,
          accountExists: true,
        });
      } else if (authErrorStatus(signinError) === 429) {
        setError(RATE_LIMITED_MESSAGE);
        trackSignupFailed({
          source,
          stage: "sign_in_after_create",
          reason: "rate_limited",
          status: 429,
          attempts: 1,
          recovered: false,
          accountExists: true,
        });
      } else {
        setError(
          "That password doesn't match this account. Try again or reset it.",
        );
        trackSignupFailed({
          source,
          stage: "sign_in_after_create",
          reason: "wrong_password",
          status: authErrorStatus(signinError),
          attempts: 1,
          recovered: false,
          accountExists: true,
        });
      }
      setLoading(false);
      return;
    }

    authCompletedRef.current = true;
    onOpenChange(false);
    router.replace(loginDestination(next));
    router.refresh();
    setLoading(false);
  }

  /**
   * Everything that must happen once a signup has produced a live session.
   *
   * Extracted so the RECOVERED path runs it too. Previously a recovery that
   * succeeded returned early and skipped this entire chain — no identify, no
   * funnel event, no Meta conversion, no referral enrolment, and above all no
   * `signInWithOtp`, which is the only thing that ever sends the verification
   * email. That left a live account stranded behind a non-dismissible
   * verification gate with no link to click.
   *
   * `provenance` says whether we can PROVE this signup created the account.
   * Attribution fires only on proof; the magic link fires either way, because a
   * brand-new account with no link is far worse than a returning user getting
   * one they can ignore.
   */
  async function completeSignup(args: {
    supabase: ReturnType<typeof createClient>;
    user: User;
    trimmedEmail: string;
    destination: string;
    emailRedirectTo: string | undefined;
    provenance: "created" | "unknown";
    recovered: boolean;
    attempts: number;
  }) {
    // One tail per modal open — guards a second submit or a recovery racing the
    // original request from double-firing any of the below.
    if (signupTailFiredRef.current) return;
    signupTailFiredRef.current = true;

    const {
      supabase,
      user,
      trimmedEmail,
      destination,
      emailRedirectTo,
      provenance,
      recovered,
      attempts,
    } = args;

    // Attribution + funnel: ONLY for an account we can prove we created. A
    // recovered session of unknown provenance may belong to an account that
    // already existed, and identifying there would alias two real PostHog
    // people together — irreversibly.
    if (provenance === "created") {
      linkUtmToUser(user.id);
      identifyUser(user.id, {
        ...buildPostHogPersonProperties(user),
        email: trimmedEmail,
      });
      trackSignup(user.id, trimmedEmail, source);
    }
    if (recovered) {
      trackSignupRecovered({ source, attempts, provenance });
    }

    fetch("/api/referrals/enrol", { method: "POST" }).catch(() => {});
    // Meta CAPI: CompleteRegistration for Panda's own ads. Fire-and-forget
    // — the endpoint resolves the user from the session cookie, so no body.
    fetch("/api/auth/track-signup", { method: "POST" }).catch(() => {});

    const refCode =
      safeGetItem("panda_ref") ||
      document.cookie.match(/panda_ref=([^;]+)/)?.[1];
    if (refCode) {
      fetch("/api/referrals/track-signup", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          code: refCode,
          referred_user_id: user.id,
        }),
      })
        .then(() => {
          safeRemoveItem("panda_ref");
          document.cookie = "panda_ref=; path=/; max-age=0";
        })
        .catch(() => {});
    }

    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
    fetch("/api/credits/timezone", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ timezone: tz }),
    }).catch(() => {});

    // Welcome email moved to /auth/confirm — fires on first email
    // verification so we don't mail typo'd / bouncing addresses.

    if (isStudentEmail(trimmedEmail)) {
      fetch("/api/student/verify").catch(() => {});
    }

    // PANDA-emailVerificationGate: fire off a magic-link email so the
    // user has a way to verify whenever they choose. With Supabase's
    // "Confirm email" toggle disabled (Aaron's setup, required for the
    // friction-free signup), the standard signUp() doesn't send any
    // email — signInWithOtp does. The link lands on /auth/confirm which
    // calls mark_email_verified() to flip our gate. Fire-and-forget so
    // a slow mailer doesn't block the redirect.
    //
    // Sent on the recovered path too, including at unknown provenance: a
    // failed signUp() wipes the PKCE code verifier, so any link generated by
    // the attempt that died is already un-exchangeable. This writes a fresh one.
    void supabase.auth
      .signInWithOtp({
        email: trimmedEmail,
        options: {
          shouldCreateUser: false,
          ...(emailRedirectTo ? { emailRedirectTo } : {}),
        },
      })
      .catch(() => {
        /* mailer hiccup; user can resend from the gate modal */
      });

    // Email signups must verify before they can use the app. Open the
    // root-mounted verification block now — passing the address we just
    // created so it shows instantly without a status round-trip — then drop
    // the user into the app behind it. The server gate (lib/auth/email-gate.ts)
    // refuses every credit spend until the magic link is clicked, and the
    // block self-reopens on any later visit while the account stays
    // unverified. Social signups never reach here (they go through
    // /auth/callback and are provider-verified).
    const target = isStudentEmail(trimmedEmail)
      ? "/?student=true"
      : destination;
    if (provenance === "created" && typeof window !== "undefined") {
      window.dispatchEvent(
        new CustomEvent("panda:email-verification-required", {
          detail: { email: trimmedEmail },
        }),
      );
    }
    authCompletedRef.current = true;
    // If the user closed the modal while we were recovering, finish the account
    // silently rather than yanking them out of whatever they moved on to.
    if (!abortedRef.current) {
      onOpenChange(false);
      router.replace(target);
    }
    router.refresh();
    setLoading(false);
  }

  /** What the recovery loop concluded about a transient signup failure. */
  type Recovery =
    | {
        kind: "signed_in";
        user: User;
        attempts: number;
      }
    | { kind: "other_user"; attempts: number }
    | { kind: "created_not_signed_in"; attempts: number; status: number | null }
    | { kind: "rate_limited"; attempts: number }
    | { kind: "offline"; attempts: number }
    | { kind: "server"; attempts: number }
    | { kind: "aborted"; attempts: number };

  /**
   * Work out what really happened after signUp() died at the network layer.
   *
   * Replaces a single immediate signInWithPassword() whose failure was reported
   * as "we couldn't reach our servers" regardless of cause. Two things changed:
   * the retry now waits for the connection to plausibly be back (retrying into
   * the same dead radio microseconds later never worked), and when it still
   * can't sign in we ASK THE SERVER whether the account exists instead of
   * guessing.
   *
   * getSession()/getUser() are deliberately not used: signUp() persists nothing
   * when its response is lost, so a local read can only surface a pre-existing
   * session — and getUser() on a 401 would actively sign the user out.
   */
  async function recoverTransientSignup(
    supabase: ReturnType<typeof createClient>,
    trimmedEmail: string,
  ): Promise<Recovery> {
    const deadline = Date.now() + RECOVERY_BUDGET_MS;
    let lastError: { name?: string; message?: string; status?: number } | null =
      null;
    let attempts = 0;

    for (let attempt = 0; attempt < 2; attempt++) {
      if (abortedRef.current) return { kind: "aborted", attempts };
      // Never retry into a dead radio or a frozen tab. Bounded by whatever is
      // left of the budget; on timeout we attempt anyway (lib/api.ts policy).
      await waitForConnectivity(
        Math.max(0, Math.min(CONNECTIVITY_WAIT_MS, deadline - Date.now())),
      );
      if (attempt > 0) {
        await new Promise((r) =>
          setTimeout(r, NETWORK_RETRY_BASE_MS * 2 ** (attempt - 1)),
        );
      }
      if (abortedRef.current) return { kind: "aborted", attempts };

      attempts++;
      const { data, error } = await withTimeout(
        supabase.auth.signInWithPassword({
          email: trimmedEmail,
          password,
        }),
        RECOVERY_ATTEMPT_TIMEOUT_MS,
        () =>
          ({
            data: { session: null, user: null },
            error: { name: "AuthRetryableFetchError", message: "timeout" },
          }) as never,
      );

      if (!error && data?.session?.user) {
        // Identity assertion: never adopt a session that isn't the address the
        // user just typed. A shared phone can already hold someone else's.
        const who = data.session.user.email?.trim().toLowerCase();
        if (who && who !== trimmedEmail.toLowerCase()) {
          return { kind: "other_user", attempts };
        }
        return { kind: "signed_in", user: data.session.user, attempts };
      }
      lastError = error ?? null;
      // A DEFINITE answer from the server ends the loop — retrying a 429, a bad
      // password or an unconfirmed email cannot change the outcome.
      if (!isTransientAuthError(lastError)) break;
      if (Date.now() >= deadline) break;
    }

    if (authErrorStatus(lastError) === 429) {
      return { kind: "rate_limited", attempts };
    }

    // The only sound way to learn whether the account exists.
    const probe = await probeAccountExists(trimmedEmail);
    if (probe === "exists") {
      return {
        kind: "created_not_signed_in",
        attempts,
        status: authErrorStatus(lastError),
      };
    }
    return { kind: isBrowserOffline() ? "offline" : "server", attempts };
  }

  async function handleSignup(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError(null);

    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      setLoading(false);
      return;
    }

    const trimmedEmail = email.trim();
    const destination = safeNext(next);
    savePostAuthRedirect(destination);
    const emailRedirectTo = buildEmailRedirectTo(destination);

    const supabase = createClient();
    // Signup asks for email + password only (no name). Seed a friendly fallback
    // display name off the email so emails, profile UI, and analytics have a
    // real, non-placeholder name from the first session — Settings → Account
    // information overwrites it once the user enters their real name. first_name
    // / last_name stay unset so that Settings prompt still reads empty.
    // Bounded: without a client-side limit this rides Safari's ~60s ceiling,
    // and a recovery stacked behind it left the button in "Creating account…"
    // for minutes. A timeout is shaped as a transient error so it flows into
    // the same recovery path as a dropped connection.
    const { data, error: signupError } = await withTimeout(
      supabase.auth.signUp({
        email: trimmedEmail,
        password,
        options: {
          data: buildFallbackNameMetadata(trimmedEmail),
          ...(emailRedirectTo ? { emailRedirectTo } : {}),
        },
      }),
      SIGNUP_REQUEST_TIMEOUT_MS,
      () =>
        ({
          data: { user: null, session: null },
          error: { name: "AuthRetryableFetchError", message: "timeout" },
        }) as never,
    );

    // Account already exists (only reached when the email-step
    // pre-flight failed open). Don't dead-end the user — try to log
    // them in with the password they just typed. If it matches, they're
    // straight in; if not, drop them on the sign-in step to correct it.
    const alreadyRegistered = (() => {
      if (signupError) {
        const msg = signupError.message.toLowerCase();
        return (
          msg.includes("already registered") ||
          msg.includes("already been registered")
        );
      }
      // Supabase returns a user with no identities and no session for a
      // duplicate email. `identities` can be ABSENT rather than [], and
      // `undefined === 0` is false — so without the `?? 0` a duplicate fell
      // through into the success branch and fired user_signed_up for an account
      // that already existed. The `!data.session` term keeps a genuine
      // confirm-on signup out of this branch.
      return !!(
        data?.user &&
        !data.session &&
        (data.user.identities?.length ?? 0) === 0
      );
    })();

    if (alreadyRegistered) {
      const { error: signinError } = await supabase.auth.signInWithPassword({
        email: trimmedEmail,
        password,
      });
      if (!signinError) {
        authCompletedRef.current = true;
        onOpenChange(false);
        router.replace(loginDestination(next));
        router.refresh();
        setLoading(false);
        return;
      }
      // This path means signUp() itself rejected the email — i.e. an
      // email/password account already exists — so the password form is
      // the right ask (clear any stale OAuth providers).
      setExistingProviders([]);
      setStep("signin");
      setError(
        "You already have an account. Enter your password to sign in.",
      );
      trackSignupFailed({
        source,
        stage: "create_account",
        reason: "already_registered",
        status: authErrorStatus(signupError),
        attempts: 1,
        recovered: false,
        accountExists: true,
      });
      setLoading(false);
      return;
    }

    if (signupError) {
      // A deterministic rejection (weak password, invalid email) is final —
      // no retry can change it. Surface it, hostname-scrubbed.
      if (!isTransientAuthError(signupError)) {
        setError(friendlySignupError(signupError.message));
        trackSignupFailed({
          source,
          stage: "create_account",
          reason: "rejected",
          status: authErrorStatus(signupError),
          attempts: 1,
          recovered: false,
          accountExists: null,
        });
        setLoading(false);
        return;
      }

      // A transient network failure ("Load failed (…supabase.co)") can fire on
      // the client even when the account was actually created server-side — the
      // request reached Supabase but the response never loaded. Work out which
      // it was instead of assuming the worst and blaming the user's connection.
      const outcome = await recoverTransientSignup(supabase, trimmedEmail);

      if (outcome.kind === "signed_in") {
        setError(null);
        await completeSignup({
          supabase,
          user: outcome.user,
          trimmedEmail,
          destination,
          emailRedirectTo,
          // Only the email-step pre-flight can prove this address had no
          // account moments ago; anything less and we must not claim a signup.
          provenance:
            preflightRef.current === "no_account" ? "created" : "unknown",
          recovered: true,
          attempts: outcome.attempts,
        });
        return;
      }

      if (outcome.kind === "aborted") {
        setLoading(false);
        return;
      }

      if (outcome.kind === "other_user") {
        setError(OTHER_USER_MESSAGE);
        trackSignupFailed({
          source,
          stage: "create_account",
          reason: "other_user_session",
          status: null,
          attempts: outcome.attempts,
          recovered: false,
          accountExists: true,
        });
      } else if (outcome.kind === "created_not_signed_in") {
        // The account IS there — we just couldn't establish the session. Say so
        // and put them one password away, instead of a connection error that is
        // both wrong and a dead end.
        setExistingProviders([]);
        setPassword("");
        setStep("signin");
        setError(ACCOUNT_CREATED_MESSAGE);
        trackSignupFailed({
          source,
          stage: "sign_in_after_create",
          reason: "account_created_signin_failed",
          status: outcome.status,
          attempts: outcome.attempts,
          recovered: false,
          accountExists: true,
        });
      } else if (outcome.kind === "rate_limited") {
        setError(RATE_LIMITED_MESSAGE);
        trackSignupFailed({
          source,
          stage: "create_account",
          reason: "rate_limited",
          status: 429,
          attempts: outcome.attempts,
          recovered: false,
          accountExists: null,
        });
      } else if (outcome.kind === "offline") {
        setError(OFFLINE_MESSAGE);
        trackSignupFailed({
          source,
          stage: "create_account",
          reason: "offline",
          status: null,
          attempts: outcome.attempts,
          recovered: false,
          accountExists: null,
        });
      } else {
        setError(SERVER_MESSAGE);
        trackSignupFailed({
          source,
          stage: "create_account",
          reason: "server_unreachable",
          status: null,
          attempts: outcome.attempts,
          recovered: false,
          accountExists: null,
        });
      }
      setLoading(false);
      return;
    }

    if (!data.user) {
      setError(
        "We could not create your account. If you already registered, sign in instead.",
      );
      trackSignupFailed({
        source,
        stage: "create_account",
        reason: "no_user",
        status: null,
        attempts: 1,
        recovered: false,
        accountExists: null,
      });
      setLoading(false);
      return;
    }

    await completeSignup({
      supabase,
      user: data.user,
      trimmedEmail,
      destination,
      emailRedirectTo,
      provenance: "created",
      recovered: false,
      attempts: 1,
    });
  }

  // An existing account with no password identity (e.g. signed up with
  // Google). We can't sign these users in with a password, so the signin
  // step offers their provider's button instead.
  const oauthOnlyProviders =
    existingProviders.length > 0 && !existingProviders.includes("email")
      ? existingProviders
      : [];

  return (
    <DialogPrimitive.Root
      open={open}
      onOpenChange={onOpenChange}
    >
      <DialogPrimitive.Portal>
        <DialogPrimitive.Backdrop className="fixed inset-0 z-[60] bg-black/40 backdrop-blur-sm duration-150 data-open:animate-in data-open:fade-in-0 data-closed:animate-out data-closed:fade-out-0 dark:bg-black/50" />
        <DialogPrimitive.Popup
          className={cn(
            // Centered modal at every breakpoint — matches the login modal
            // pattern. Mobile keeps 16px outer padding per Figma, desktop
            // bumps to 36px. Dark-mode bg uses background-/-default token
            // (#111522) per Figma node 1696:16735.
            "fixed left-1/2 top-1/2 z-70 flex max-h-[calc(100dvh-2rem)] w-[calc(100%-2rem)] max-w-115 -translate-x-1/2 -translate-y-1/2 flex-col overflow-y-auto rounded-3xl bg-white px-4 pb-6 pt-2 shadow-2xl outline-none duration-150 sm:px-9 sm:pb-9 sm:pt-4 dark:bg-[#111522]",
            "data-open:animate-in data-open:fade-in-0 data-open:zoom-in-95 data-closed:animate-out data-closed:fade-out-0 data-closed:zoom-out-95",
            fontTight,
          )}
        >
          {/* Top bar (Figma: h-14, X icon 24x24 at top-right, 16px padding).
              Step 2 keeps a back affordance on the left. */}
          <div className="flex h-14 w-full items-center justify-between">
            {step === "details" || step === "signin" ? (
              <button
                type="button"
                onClick={handleBackToEmail}
                className="flex h-6 w-6 items-center justify-center rounded-full text-[#1A1A1B] transition-opacity hover:opacity-70 sm:h-9 sm:w-9 sm:hover:bg-zinc-100 dark:text-[#cbd5e1] dark:sm:hover:bg-[#18212f]"
                aria-label="Back"
              >
                <ArrowLeft className="h-6 w-6 sm:h-5 sm:w-5" strokeWidth={2} />
              </button>
            ) : (
              <span aria-hidden className="h-6 w-6 sm:h-9 sm:w-9" />
            )}
            <DialogPrimitive.Close
              className="flex h-6 w-6 items-center justify-center rounded-full text-[#1A1A1B] transition-opacity hover:opacity-70 sm:h-9 sm:w-9 sm:hover:bg-zinc-100 dark:text-[#cbd5e1] dark:sm:hover:bg-[#18212f]"
              aria-label="Close"
            >
              <X className="h-6 w-6 sm:h-5 sm:w-5" strokeWidth={2} />
            </DialogPrimitive.Close>
          </div>

          {/* Content starts ~38px below top bar per Figma (y=94 - top bar 56) */}
          <div className="mt-2.5 sm:mt-1" />

          {/* Brand mark — avatar covers its eyes while a password field is focused */}
          <div className="flex w-full justify-center">
            <PandaAuthAvatar eyesClosed={passwordFocused} />
          </div>

          {step === "email" ? (
            <>
              {/* Title + subtitle. Mobile: 24/32 + 14/20 with gap-1.
                  Desktop: existing 28px + 14px with gap-2. */}
              <div className="mt-4 flex w-full flex-col items-center gap-1 text-center sm:mt-5 sm:gap-2">
                <DialogPrimitive.Title className="font-[family-name:var(--font-rubik),sans-serif] text-[24px] font-medium leading-8 text-[#1A1A1B] dark:text-[#f1f5f9] sm:font-[family-name:var(--font-inter-tight),sans-serif] sm:text-[28px] sm:font-bold sm:leading-[1.2] sm:text-[#101010] sm:dark:text-[#f1f5f9]">
                  Let&apos;s get started
                </DialogPrimitive.Title>
                <DialogPrimitive.Description className="font-[family-name:var(--font-rubik),sans-serif] text-sm font-normal leading-5 text-[#77869D] dark:text-[#94a3b8] sm:font-[family-name:var(--font-inter-tight),sans-serif] sm:leading-normal sm:text-[#6B7280] sm:dark:text-[#94a3b8]">
                  Log in or create an account to start building.
                </DialogPrimitive.Description>
              </div>

              {/* 20px gap to social buttons per Figma (mt-5) */}
              <div className="mt-5 flex w-full flex-col gap-3 sm:mt-6">
                <GoogleSignInButton
                  label="Continue with Google"
                  buttonClassName={socialBtnClass}
                  mode="signup"
                  next={next}
                />
                <FacebookSignInButton
                  label="Continue with Facebook"
                  buttonClassName={socialBtnClass}
                  mode="signup"
                  next={next}
                />
                <LinkedInSignInButton
                  label="Continue with LinkedIn"
                  buttonClassName={socialBtnClass}
                  mode="signup"
                  next={next}
                />
              </div>

              {/* "or" divider — Figma: line #CBD5E1, label 16/24 #77869D */}
              <div className="mt-5 flex w-full items-center gap-3 sm:mt-6">
                <div className="min-h-0 min-w-0 flex-1 border-t border-[#CBD5E1] dark:border-[#334155] sm:border-[#E5E7EB] sm:dark:border-[#334155]" />
                <span className="shrink-0 font-[family-name:var(--font-rubik),sans-serif] text-base font-normal leading-6 text-[#77869D] dark:text-[#94a3b8] sm:font-[family-name:var(--font-inter-tight),sans-serif] sm:text-[13px] sm:leading-normal sm:text-[#9CA3AF] sm:dark:text-[#94a3b8]">
                  or
                </span>
                <div className="min-h-0 min-w-0 flex-1 border-t border-[#CBD5E1] dark:border-[#334155] sm:border-[#E5E7EB] sm:dark:border-[#334155]" />
              </div>

              <form
                onSubmit={handleEmailContinue}
                className="mt-5 flex w-full flex-col gap-4"
              >
                <input
                  type="email"
                  autoComplete="email"
                  placeholder="Email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (error) setError(null);
                    // Editing invalidates any prior "did you mean" hint; it's
                    // recomputed on blur.
                    if (emailSuggestion) setEmailSuggestion(null);
                  }}
                  className={inputClass}
                  required
                />

                {error ? (
                  <p role="status" aria-live="polite" className="font-[family-name:var(--font-rubik),sans-serif] text-sm font-normal leading-5 text-[#dc2626] dark:text-[#fda4af]">{error}</p>
                ) : emailSuggestion ? (
                  <p className="font-[family-name:var(--font-rubik),sans-serif] text-sm font-normal leading-5 text-[#77869D] dark:text-[#94a3b8]">
                    Did you mean{" "}
                    <button
                      type="button"
                      onClick={() => {
                        setEmail(emailSuggestion);
                        setEmailSuggestion(null);
                      }}
                      className="font-medium text-emerald-700 underline underline-offset-2 transition-colors hover:text-emerald-800 dark:text-emerald-400 dark:hover:text-emerald-300"
                    >
                      {emailSuggestion}
                    </button>
                    ?
                  </p>
                ) : null}

                <button
                  type="submit"
                  disabled={loading || !email.trim()}
                  className={primaryBtnClass}
                >
                  {loading ? "Checking…" : "Continue"}
                </button>
              </form>
            </>
          ) : step === "details" ? (
            <>
              {/* Title + subtitle (Figma node 2350:42147).
                  Mobile: 24/32 + 14/20 with gap-1; desktop keeps
                  existing 28px Inter Tight with gap-2. */}
              <div className="mt-4 flex w-full flex-col items-center gap-1 text-center sm:mt-5 sm:gap-2">
                <DialogPrimitive.Title className="font-[family-name:var(--font-rubik),sans-serif] text-[24px] font-medium leading-8 text-[#1A1A1B] dark:text-[#f1f5f9] sm:font-[family-name:var(--font-inter-tight),sans-serif] sm:text-[28px] sm:font-bold sm:leading-[1.2] sm:text-[#101010] sm:dark:text-[#f1f5f9]">
                  Create an account
                </DialogPrimitive.Title>
                <DialogPrimitive.Description className="font-[family-name:var(--font-rubik),sans-serif] text-sm font-normal leading-5 text-[#77869D] dark:text-[#94a3b8] sm:font-[family-name:var(--font-inter-tight),sans-serif] sm:leading-normal sm:text-[#6B7280] sm:dark:text-[#94a3b8]">
                  You&apos;re almost there! Choose a password to create your
                  account for{" "}
                  <span className="font-medium text-[#1A1A1B] dark:text-[#f1f5f9] sm:font-semibold sm:text-[#101010] sm:dark:text-[#f1f5f9]">
                    {email}
                  </span>
                  .
                </DialogPrimitive.Description>
              </div>

              <form
                onSubmit={handleSignup}
                className="mt-5 flex w-full flex-col gap-3 sm:mt-6 sm:gap-4"
              >
                <div>
                  <div className="relative w-full">
                    <input
                      type={showPassword ? "text" : "password"}
                      name="password"
                      autoComplete="new-password"
                      placeholder="Password"
                      value={password}
                      onChange={(e) => {
                        setPassword(e.target.value);
                        // Matches the email and sign-in steps: a failure message
                        // pinned under a field the user is actively correcting
                        // reads as a live error, and can end up as the only
                        // explanation for a disabled button.
                        if (error) setError(null);
                      }}
                      onFocus={() => setPasswordFocused(true)}
                      onBlur={handlePwBlur}
                      className={inputWithIconClass}
                      minLength={8}
                      required
                    />
                    <EyeToggle
                      visible={showPassword}
                      onToggle={() => setShowPassword((v) => !v)}
                    />
                  </div>
                  {/* Figma node 2350:42157: 14/20 #77869D, 16px left
                      padding (aligns with field's inner left edge). */}
                  <p className="mt-2 pl-4 font-[family-name:var(--font-rubik),sans-serif] text-sm font-normal leading-5 text-[#77869D] dark:text-[#94a3b8] sm:pl-0 sm:font-[family-name:var(--font-inter-tight),sans-serif] sm:text-[12.5px] sm:leading-normal sm:text-[#9CA3AF] sm:dark:text-[#94a3b8]">
                    At least 8 characters. Longer is stronger.
                  </p>
                </div>

                {error ? (
                  <p role="status" aria-live="polite" className="font-[family-name:var(--font-rubik),sans-serif] text-sm font-normal leading-5 text-[#dc2626] dark:text-[#fda4af]">{error}</p>
                ) : null}

                <button
                  type="submit"
                  disabled={loading || password.length < 8}
                  className={primaryBtnClass}
                >
                  {loading ? "Creating account…" : "Create an account"}
                </button>
              </form>
            </>
          ) : (
            <>
              {/* Sign-in step — reached when the email already has an
                  account. We keep the user in the same modal and just
                  collect their password to log them in (handleSignin),
                  rather than sending them off to a separate login page. */}
              <div className="mt-4 flex w-full flex-col items-center gap-1 text-center sm:mt-5 sm:gap-2">
                <DialogPrimitive.Title className="font-[family-name:var(--font-rubik),sans-serif] text-[24px] font-medium leading-8 text-[#1A1A1B] dark:text-[#f1f5f9] sm:font-[family-name:var(--font-inter-tight),sans-serif] sm:text-[28px] sm:font-bold sm:leading-[1.2] sm:text-[#101010] sm:dark:text-[#f1f5f9]">
                  Welcome back
                </DialogPrimitive.Title>
                <DialogPrimitive.Description className="font-[family-name:var(--font-rubik),sans-serif] text-sm font-normal leading-5 text-[#77869D] dark:text-[#94a3b8] sm:font-[family-name:var(--font-inter-tight),sans-serif] sm:leading-normal sm:text-[#6B7280] sm:dark:text-[#94a3b8]">
                  {oauthOnlyProviders.length > 0 ? (
                    <>
                      You already signed up with{" "}
                      {oauthOnlyProviders.length === 1
                        ? providerLabel(oauthOnlyProviders[0])
                        : "a connected account"}
                      . Continue to sign in as{" "}
                    </>
                  ) : (
                    <>
                      You already have an account. Enter your password to sign
                      in as{" "}
                    </>
                  )}
                  <span className="font-medium text-[#1A1A1B] dark:text-[#f1f5f9] sm:font-semibold sm:text-[#101010] sm:dark:text-[#f1f5f9]">
                    {email}
                  </span>
                  .
                </DialogPrimitive.Description>
              </div>

              {oauthOnlyProviders.length > 0 ? (
                <div className="mt-5 flex w-full flex-col gap-3 sm:mt-6">
                  {oauthOnlyProviders.map((p) => {
                    if (p === "google") {
                      return (
                        <GoogleSignInButton
                          key={p}
                          label="Continue with Google"
                          buttonClassName={socialBtnClass}
                          mode="login"
                        />
                      );
                    }
                    if (p === "facebook") {
                      return (
                        <FacebookSignInButton
                          key={p}
                          label="Continue with Facebook"
                          buttonClassName={socialBtnClass}
                          mode="login"
                        />
                      );
                    }
                    if (p === "linkedin_oidc" || p === "linkedin") {
                      return (
                        <LinkedInSignInButton
                          key={p}
                          label="Continue with LinkedIn"
                          buttonClassName={socialBtnClass}
                          mode="login"
                        />
                      );
                    }
                    return null;
                  })}
                </div>
              ) : (
                <form
                  onSubmit={handleSignin}
                  className="mt-5 flex w-full flex-col gap-3 sm:mt-6 sm:gap-4"
                >
                  <div className="relative w-full">
                    <input
                      type={showPassword ? "text" : "password"}
                      name="password"
                      autoComplete="current-password"
                      placeholder="Password"
                      value={password}
                      onChange={(e) => {
                        setPassword(e.target.value);
                        if (error) setError(null);
                      }}
                      onFocus={() => setPasswordFocused(true)}
                      onBlur={handlePwBlur}
                      className={inputWithIconClass}
                      required
                    />
                    <EyeToggle
                      visible={showPassword}
                      onToggle={() => setShowPassword((v) => !v)}
                    />
                  </div>

                  {error ? (
                    <p role="status" aria-live="polite" className="font-[family-name:var(--font-rubik),sans-serif] text-sm font-normal leading-5 text-[#dc2626] dark:text-[#fda4af]">{error}</p>
                  ) : null}

                  <button
                    type="submit"
                    disabled={loading || !password}
                    className={primaryBtnClass}
                  >
                    {loading ? "Signing in…" : "Sign in"}
                  </button>

                  <Link
                    href={`/forgot-password?next=${encodeURIComponent(next)}`}
                    className="flex h-12 w-full items-center justify-center font-[family-name:var(--font-rubik),sans-serif] text-base font-medium leading-6 text-emerald-700 hover:opacity-70 dark:text-emerald-400 sm:font-[family-name:var(--font-inter-tight),sans-serif] sm:font-semibold sm:text-emerald-700 sm:hover:text-emerald-800 sm:hover:opacity-100 sm:dark:text-emerald-400"
                  >
                    Forgot password?
                  </Link>
                </form>
              )}
            </>
          )}

          {/* Terms & Privacy footer — Figma node 1696:16752. Links share
              the muted footer color/weight (text-tertiary #94a3b8 dark),
              no distinct emphasis. Light mirrors the same role tokens. */}
          <p className="mt-5 text-center font-[family-name:var(--font-rubik),sans-serif] text-sm font-normal leading-5 text-[#77869D] dark:text-[#94a3b8] sm:font-[family-name:var(--font-inter-tight),sans-serif] sm:text-[12.5px] sm:leading-normal sm:text-[#9CA3AF] sm:dark:text-[#94a3b8]">
            By continuing, you have read and agree to our{" "}
            <Link
              href="/terms"
              className="font-normal text-[#77869D] underline-offset-2 hover:underline dark:text-[#94a3b8] sm:text-[#9CA3AF] sm:dark:text-[#94a3b8]"
            >
              Terms and Conditions
            </Link>{" "}
            and{" "}
            <Link
              href="/privacy"
              className="font-normal text-[#77869D] underline-offset-2 hover:underline dark:text-[#94a3b8] sm:text-[#9CA3AF] sm:dark:text-[#94a3b8]"
            >
              Privacy Policy
            </Link>
            .
          </p>
        </DialogPrimitive.Popup>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
